import { useQueryNormalizer } from '@normy/react-query';
import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { resolveLabels } from '@/features/sources/lib/resolveLabels.lib';
import { gqlClient } from '@/lib';
import type { ExperienceFeed } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import type {
  UpdateSourceForExperienceMutation,
  UpdateSourceForExperienceMutationVariables,
} from './useUpdateSource.generated';

const query = gql`
  mutation UpdateSourceForExperience($input: UpdateSourceForExperienceInput!) {
    updateSourceForExperience(input: $input) {
      kind
      id
      title
      originalTitle
      color
      emoji
      category {
        id
        text
        color
        colorName
        sortOrder
      }
      labels {
        id
        text
        sortOrder
      }
      whoLabels {
        id
        text
        sortOrder
      }
      syncedAt
      error
    }
  }
`;

export const useUpdateSource = () => {
  const { experience } = usePageExperienceContext();
  const queryNormalizer = useQueryNormalizer();

  const { mutate, isPending } = useMutation<
    UpdateSourceForExperienceMutation,
    QueryError,
    UpdateSourceForExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        UpdateSourceForExperienceMutation,
        UpdateSourceForExperienceMutationVariables
      >(query, variables),
    onMutate: (variables) => {
      const {
        sourceId,
        color,
        title,
        emoji,
        categoryId,
        labelIds,
        whoLabelIds,
      } = variables.input;
      const prevValue = queryNormalizer.getObjectById<ExperienceFeed>(sourceId);

      // Color/title resets (null) skip optimistic updates — let the server
      // settle the row with the original values. A null categoryId or emoji is
      // a real unassign, so both are applied optimistically below.
      if (color === null || title === null) {
        return { rollbackData: prevValue };
      }

      // Resolve the selected category so the assigned name/color reflect
      // immediately (the normalized source stores `category`, not `categoryId`).
      const resolveCategory = () => {
        if (categoryId === undefined) return undefined;
        if (categoryId === null) return null;
        return (
          experience.categories.find((c) => c.id === categoryId) ?? undefined
        );
      };
      const category = resolveCategory();
      const labels = resolveLabels(labelIds, experience.labels);
      const whoLabels = resolveLabels(whoLabelIds, experience.whoLabels);

      return {
        rollbackData: prevValue,
        optimisticData: {
          id: sourceId,
          ...(color !== undefined && { color }),
          ...(title !== undefined && { title }),
          ...(emoji !== undefined && { emoji }),
          ...(category !== undefined && { category }),
          ...(labels !== undefined && { labels }),
          ...(whoLabels !== undefined && { whoLabels }),
        },
      };
    },
  });

  const updateSource = (
    {
      sourceId,
      color,
      title,
      emoji,
      categoryId,
      labelIds,
      whoLabelIds,
    }: {
      sourceId: string;
      color?: string | null;
      title?: string | null;
      emoji?: string | null;
      categoryId?: string | null;
      labelIds?: string[];
      whoLabelIds?: string[];
    },
    options?: MutateOptions<
      UpdateSourceForExperienceMutation,
      QueryError,
      UpdateSourceForExperienceMutationVariables
    >
  ) => {
    mutate(
      {
        input: {
          experienceId: experience.id,
          sourceId,
          color,
          title,
          emoji,
          categoryId,
          labelIds,
          whoLabelIds,
        },
      },
      options
    );
  };

  return { updateSource, isPending };
};
